import { isEmpty } from 'lodash';
import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useRevalidator } from 'react-router';

import { useQueryParameters } from '@app/components/HigherOrderComponents/use-query-parameters';
import MarleyIcon from '@app/components/icons/MarleyIcon';
import Loading from '@app/components/Loading/loading-simple';
import { FAILURE, SUCCESS } from '@app/constants/general';
import { checkInitDone } from '@app/helpers/common';
import isLoadedFromMobile from '@app/helpers/platform';
import { useSafeAuth } from '@app/hooks/use-safe-auth';
import { StateType } from '@app/types/reducer-state';

import { ErrorMessage } from './error-message';
import {
  useAuthenticationRedirect,
  useAuthInitialization,
  useClearMediaCache,
  useLogoutHandler,
  useWebSocketConnection,
} from './hooks';

export const UserInit = () => {
  const { authState } = useSafeAuth();

  const { logout, authId, loadingStatus, accessTokenErrorMessage } =
    useSelector((state: StateType) => ({
      logout: state?.auth?.logout,
      authId: state?.auth?.user?._id,
      loadingStatus: checkInitDone(state),
      accessTokenErrorMessage: state.auth.accessTokenErrorMessage,
    }));

  useAuthInitialization();
  useWebSocketConnection();
  useClearMediaCache(authId);
  useLogoutHandler(logout);

  useQueryParameters();

  const loginId = authState?.idToken?.claims?.preferred_username;

  // Error message logic
  let message;
  if (loadingStatus === FAILURE) {
    message = 'There was an error loading the page';
  } else if (loadingStatus === 'health-check-failure') {
    message = 'Health check failure';
  } else if (loadingStatus === 'user-lookup-failure') {
    message = 'Error fetching user information';
  } else if (loadingStatus === 'websockets-failure') {
    message = 'WebSocket initialization failure';
  }

  if (accessTokenErrorMessage) {
    message = accessTokenErrorMessage;
  }

  // Render logic
  if (message) {
    return (
      <div className="splashScreen">
        <div className="logo-container">
          <MarleyIcon />
        </div>
        <ErrorMessage message={message} loginId={loginId} />
      </div>
    );
  }

  // this is for preventing the loading screen on logout
  if (loadingStatus === SUCCESS && logout) {
    return null;
  }

  // Show loading overlay until authentication is complete
  const isLoading = loadingStatus !== SUCCESS && !logout;

  return isLoading ? (
    <Loading />
  ) : (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  );
};

export const RequiredAuth = () => {
  const { authId, inboxFilters } = useSelector((state: StateType) => ({
    authId: state?.auth?.user?._id,
    inboxFilters: state?.profile?.properties?.inboxFilters,
  }));

  const revalidator = useRevalidator();

  const isInboxFiltersAvailable = !isEmpty(inboxFilters);

  // Trigger early API calls with revalidate when authId & inbox filters are ready.
  // Without this, React Router runs the loader later, delaying data fetches.
  // On mobile we skip revalidate since the way web view works can cause infinite loop.
  // (Long-term fix: move authId retrieval to react-query so the loader can await it and eliminate this hook
  useEffect(() => {
    if (authId && isInboxFiltersAvailable && !isLoadedFromMobile()) {
      revalidator.revalidate();
    }
  }, [authId, isInboxFiltersAvailable, revalidator]);

  // Keep UserInit separate from RequiredAuth to avoid running auth-dependent hooks
  // before the JWT is available. We can’t use a loader since Okta’s authState starts
  // as null and updates asynchronously; with components we can return null to block
  // rendering, but loaders still render children. Awaiting authState isn’t possible
  // as Okta provides no API for it.

  const { authState } = useAuthenticationRedirect();
  if (!authState?.isAuthenticated) {
    return null;
  }
  return <UserInit />;
};
